import React from 'react'
import { isNumber } from 'lodash-es'
import { TradingEntityAssignmentDetail, FundAssignment, TradingType, UserPreference, FundTradingEntityAssignment } from 'api'
import {
  Required,
  Allocated,
  AllocatedValue,
  Remaining,
  Heading,
  ScrollContainer,
  AllocationTable,
  FillOrTrimContainer,
  RemoveAllocationButton,
  NoEntities,
  FillOrTrimButton
} from '../allocationDialogStyles'
import { NumericInput, TextInput } from 'view/components/inputs'
import { ValidationIssuesContainer } from 'view/shared/error'
import { getNotionalFormatOptions } from 'view/shared/datagrid'
import { TradingTypeHelper } from 'model'
import { Locale, Translation } from 'locale'
import { iconSet } from 'view/components/icons'
import * as constants from 'view/styles/constants'

interface AssignmentPanelProps {
  totalAmountRequired: number
  assignmentDetails: TradingEntityAssignmentDetail[]
  fundAssignment: FundAssignment
  tradingType: TradingType
  userPreference: UserPreference
  onRemoveAssignment: (assignment: FundTradingEntityAssignment, idx: number) => void
  onTradingEntityAssignmentChange: (
    tradingEntityId: string,
    updatedPartialAssignment: Partial<FundTradingEntityAssignment>,
    idx: number
  ) => void
  onSetTrimOrFill: (index: number) => void
}

export const AssignmentPanel: React.FC<AssignmentPanelProps> = props => {
  const {
    totalAmountRequired,
    assignmentDetails,
    fundAssignment: { remainingAssignmentRequired, amountAssigned, validationIssues },
    tradingType,
    userPreference,
    onTradingEntityAssignmentChange,
    onRemoveAssignment,
    onSetTrimOrFill
  } = props

  const availableAssignmentDetails = assignmentDetails.filter(assignmentDetail => assignmentDetail.tradingEntityAssignment.selected)
  const fullyAssigned = remainingAssignmentRequired === 0
  const overAssigned = isNumber(remainingAssignmentRequired) && remainingAssignmentRequired < 0
  const generalValidationIssues = (validationIssues ?? []).filter(validationIssue => validationIssue.fieldName !== 'amountAssigned')
  const notionalFormatOptions = getNotionalFormatOptions(userPreference.amountFormat, false)
  const assignmentFormatOptions = tradingType === TradingType.CASH ? { minDp: 2 } : getNotionalFormatOptions(userPreference.amountFormat)
  const amountFormatOptions = tradingType === TradingType.CASH ? { amountFormat: 'other' } : { amountFormat: userPreference.amountFormat }

  return (
    <>
      <Required fullyAllocated={fullyAssigned}>
        <span>
          {tradingType && TradingTypeHelper.getName(tradingType)}{' '}
          {Locale.translate(Translation.TC_ASSIGNMENT_REQUIRED, amountFormatOptions)}
        </span>
        <span>{Locale.formatNumber(totalAmountRequired, assignmentFormatOptions)}</span>
      </Required>
      <Allocated fullyAllocated={fullyAssigned}>
        <span>{Locale.translate(Translation.TC_AMOUNT_ASSIGNED, amountFormatOptions)}</span>
        <AllocatedValue fullyAllocated={fullyAssigned}>{Locale.formatNumber(amountAssigned || 0, assignmentFormatOptions)}</AllocatedValue>
      </Allocated>
      <Remaining overAllocated={overAssigned}>
        <span>{Locale.translate(Translation.TC_REMAINING_ASSIGNMENT_REQUIRED, amountFormatOptions)}</span>
        <span>{Locale.formatNumber(remainingAssignmentRequired || 0, assignmentFormatOptions)}</span>
      </Remaining>
      <Heading>
        <span>
          {Locale.translate(Translation.TC_ASSIGNED_TRADING_ENTITIES, { tradingEntityCount: availableAssignmentDetails.length })}:
        </span>
        {generalValidationIssues && (
          <ValidationIssuesContainer data-test-id={'FundAssignmentDialog-ValidationIssuesContainer'}>
            {generalValidationIssues.map((validationIssue, index) => (
              <li key={index}>{validationIssue.errorMessage}</li>
            ))}
          </ValidationIssuesContainer>
        )}
      </Heading>
      {availableAssignmentDetails.length > 0 ? (
        <ScrollContainer maxHeight={350}>
          <AllocationTable data-test-id="FundAssignmentDialog-AllocationTable">
            <thead>
              <tr>
                <th>{Locale.translate(Translation.TC_TRADING_ENTITY)}</th>
                <th>{Locale.translate(Translation.TC_NOTIONAL, { amountFormat: userPreference.amountFormat })}</th>
                <th>{Locale.translate(Translation.TC_ABBR_START_CONSIDERATION)}</th>
                <th>{Locale.translate(Translation.TC_BOOK)}</th>
                <th>{Locale.translate(Translation.TC_STRATEGY)}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {assignmentDetails.map((assignmentDetail, idx) => {
                const {
                  tradingEntity: { publishedName, id },
                  tradingEntityAssignment: { notional, startConsideration, selected, initiatorBook, initiatorStrategy },
                  fieldValidationIssues
                } = assignmentDetail

                const onAssignmentChange = (value: Partial<FundTradingEntityAssignment>) => {
                  onTradingEntityAssignmentChange(id, value, idx)
                }

                if (!selected) return null

                return (
                  <tr data-test-id={`AssignmentPanel-AssignmentRow-${publishedName}`} key={`${id}-${idx}`}>
                    <td style={{ width: 300 }}>{publishedName}</td>
                    {tradingType === TradingType.NOTIONAL ? (
                      <td>
                        <FillOrTrimContainer>
                          <NumericInput
                            dataTestId={'AssignmentPanel-NumericInput-Notional'}
                            value={notional}
                            onUpdate={value => onAssignmentChange({ notional: value })}
                            validationIssues={fieldValidationIssues.notional}
                            formatOptions={notionalFormatOptions}
                          />
                          <FillOrTrimButton
                            dataTestId={'AssignmentPanel-FillOrTrimButton-Notional'}
                            disabled={fullyAssigned}
                            onClick={() => onSetTrimOrFill(idx)}>
                            {iconSet.fillTrimIcon}
                          </FillOrTrimButton>
                        </FillOrTrimContainer>
                      </td>
                    ) : (
                      <td style={{ width: 200 }}>{Locale.formatNumber(notional, notionalFormatOptions)}</td>
                    )}
                    {tradingType === TradingType.CASH ? (
                      <td>
                        <FillOrTrimContainer>
                          <NumericInput
                            dataTestId={'AssignmentPanel-NumericInput-StartConsideration'}
                            value={startConsideration}
                            onUpdate={value => onAssignmentChange({ startConsideration: value })}
                            validationIssues={fieldValidationIssues.startConsideration}
                            formatOptions={{ minDp: 2 }}
                          />
                          <FillOrTrimButton
                            dataTestId={'AssignmentPanel-FillOrTrimButton-Cash'}
                            disabled={fullyAssigned}
                            onClick={() => onSetTrimOrFill(idx)}>
                            {iconSet.fillTrimIcon}
                          </FillOrTrimButton>
                        </FillOrTrimContainer>
                      </td>
                    ) : (
                      <td style={{ width: 200 }}>{Locale.formatNumber(startConsideration, { minDp: 2 })}</td>
                    )}
                    <td>
                      <TextInput
                        dataTestId={'AssignmentPanel-TextInput-Book'}
                        value={initiatorBook}
                        maxLength={constants.internalBookingMaxFieldLength}
                        onUpdate={value => onAssignmentChange({ initiatorBook: value })}
                      />
                    </td>
                    <td>
                      <TextInput
                        dataTestId={'AssignmentPanel-TextInput-Strategy'}
                        value={initiatorStrategy}
                        maxLength={constants.internalBookingMaxFieldLength}
                        onUpdate={value => onAssignmentChange({ initiatorStrategy: value })}
                      />
                    </td>
                    <td>
                      <RemoveAllocationButton
                        dataTestId={'AssignmentPanel-RemoveAssignmentButton'}
                        onClick={() => onRemoveAssignment(assignmentDetail.tradingEntityAssignment, idx)}>
                        {iconSet.crossIcon}
                      </RemoveAllocationButton>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </AllocationTable>
        </ScrollContainer>
      ) : (
        <NoEntities>{Locale.translate(Translation.SC_SELECT_ASSIGNMENTS)}</NoEntities>
      )}
    </>
  )
}
